import {useCallback, useState} from 'react';

import notifications from 'notifications';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import useStableValue from 'ui-library/hooks/useStableValue';
import * as logging from 'util/Logging';

import {GetTableDataFn} from './extractTableDataForSuggestions';
import {openCustomPlotDraftInChat, runSuggestPlotPromptInChat} from './runSuggestPlotPromptInChat';
import {PlotSuggestion} from './SuggestPlotsConstants';
import {
  DEFAULT_SUGGEST_PLOTS_STATE,
  fetchAndStoreSuggestPlots,
  getSuggestPlotsCacheKey,
  suggestPlotsGlobalState,
} from './suggestPlotsState';

export interface UseSuggestPlotsParams {
  entryId: string;
  tableName: string;
  getTableData: GetTableDataFn;
}

export interface UseSuggestPlotsReturn {
  isLoading: boolean;
  suggestions: Array<PlotSuggestion>;
  errorMessage: string | null;
  launchingSuggestionKey: string | null;
  fetchSuggestions: () => Promise<void>;
  handleSuggestionClick: (suggestion: PlotSuggestion) => void;
  handleCustomClick: () => void;
}

export default function useSuggestPlots({
  entryId,
  tableName,
  getTableData,
}: UseSuggestPlotsParams): UseSuggestPlotsReturn {
  const cacheKey = getSuggestPlotsCacheKey(entryId, tableName);
  const [state] = suggestPlotsGlobalState.useGlobalClientState(cacheKey);
  const {isLoading, suggestions, errorMessage} = state;

  const [launchingSuggestionKey, setLaunchingSuggestionKey] = useState<string | null>(null);
  const mountedRef = useIsNotUnmounted();
  const getTableDataFn = useStableValue<GetTableDataFn>(getTableData);
  const launchingSuggestionKeyValue = useStableValue<string | null>(launchingSuggestionKey);
  const suggestionsValue = useStableValue<Array<PlotSuggestion>>(suggestions);

  const fetchSuggestions = useCallback(async (): Promise<void> => {
    suggestPlotsGlobalState.updateState(cacheKey, DEFAULT_SUGGEST_PLOTS_STATE);
    await fetchAndStoreSuggestPlots({
      entryId,
      tableName,
      getTableData: getTableDataFn,
    });
  }, [entryId, tableName, cacheKey, getTableDataFn]);

  const launchChatAction = useCallback(
    async (key: string, action: () => Promise<unknown>) => {
      if (launchingSuggestionKeyValue != null) {
        return;
      }
      setLaunchingSuggestionKey(key);
      try {
        await action();
      } catch (err) {
        if (!mountedRef.current) {
          throw err;
        }
        logging.error(err, {
          extra: {message: `useSuggestPlots failed: ${key}`},
        });
        notifications.error('Could not open AI chat. Please try again.');
      } finally {
        if (mountedRef.current) {
          setLaunchingSuggestionKey(null);
        }
      }
    },
    [mountedRef, launchingSuggestionKeyValue]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: PlotSuggestion) => {
      const idx = suggestionsValue.indexOf(suggestion);
      const key = `${idx}-${suggestion.title}`;
      launchChatAction(key, () => runSuggestPlotPromptInChat({suggestion, tableName, entryId}));
    },
    [tableName, entryId, launchChatAction, suggestionsValue]
  );

  const handleCustomClick = useCallback(
    () => launchChatAction('custom', () => openCustomPlotDraftInChat({tableName, entryId})),
    [tableName, entryId, launchChatAction]
  );

  return {
    isLoading,
    suggestions,
    errorMessage,
    launchingSuggestionKey,
    fetchSuggestions,
    handleSuggestionClick,
    handleCustomClick,
  };
}
